import React, { useState, useEffect } from 'react';
import {
  Menu,
  X,
  Sparkles,
  ArrowUpRight,
  Phone,
  Mail,
} from 'lucide-react';
import { SimatechLogo } from './SimatechLogo';
import { useCms } from '../context/CmsContext';

interface HeaderProps {
  currentPage?: 'home' | 'about' | 'services' | 'industry' | 'product' | 'clients' | 'contact' | 'admin' | 'siemens-solution-partner';
  onNavigate?: (page: any, targetId?: string) => void;
  onOpenInquiry: (subject?: string) => void;
  onOpenConnect?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  currentPage = 'home',
  onNavigate,
  onOpenInquiry,
  onOpenConnect,
}) => {
  const { siteData } = useCms();
  const brand = siteData?.brand;
  const navItems = siteData?.navigation || [];
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isSolidNav = isScrolled;

  const getNavHref = (pageStr: string) => {
    if (pageStr.startsWith('http://') || pageStr.startsWith('https://')) return pageStr;
    if (pageStr.startsWith('#')) return `/${pageStr}`;
    return pageStr === 'home' || pageStr === '/' ? '/' : `/${pageStr.replace(/^\//, '')}`;
  };

  const isNavItemActive = (pageStr: string) => {
    if (currentPage === 'home' && (pageStr === 'home' || pageStr === '/' || pageStr === '')) return true;
    return currentPage === pageStr.replace(/^\//, '');
  };

  const handleNavItemClick = (e: React.MouseEvent, pageStr: string) => {
    setIsMobileMenuOpen(false);
    if (pageStr.startsWith('http://') || pageStr.startsWith('https://')) {
      return;
    }
    e.preventDefault();
    if (pageStr.startsWith('#') || pageStr.startsWith('/#')) {
      const secId = pageStr.replace('/#', '').replace('#', '');
      if (onNavigate) {
        onNavigate('home', secId);
      }
      const el = document.getElementById(secId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    const cleanPage = (pageStr.replace(/^\//, '') || 'home') as any;
    if (onNavigate) {
      onNavigate(cleanPage, cleanPage === 'home' ? 'home' : undefined);
    }
  };

  const handleLink = (e: React.MouseEvent, page: 'home' | 'about' | 'services' | 'industry' | 'product' | 'clients' | 'contact' | 'siemens-solution-partner', targetId?: string) => {
    e.preventDefault();
    if (onNavigate) {
      onNavigate(page, targetId);
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* Top Pre-Header Bar: ISO Certification, Website, Phone, and Email */}
      <div
        className="fixed top-0 left-0 right-0 z-50 h-9 transition-all duration-300 pointer-events-auto border-b border-[#0f4650] text-white shadow-xs"
        style={{
          backgroundColor: '#166572',
        }}
      >
        <div className="max-w-[1440px] mx-auto px-4 sm:px-8 md:px-12 lg:px-16 h-full pointer-events-auto">
          <div className="flex items-center justify-between h-full text-[11.5px] sm:text-[12.5px] md:text-[13px] font-normal tracking-wide">
            {/* Left: ISO Certification */}
            <div className="flex items-center gap-3 sm:gap-7 md:gap-9">
              <span className="text-white/95 font-medium whitespace-nowrap text-[11px] sm:text-[12.5px]">
                {brand?.isoCert || 'An ISO 9001 : 2015 Certified Company'}
              </span>
            </div>

            {/* Right: Phone & Email */}
            <div className="flex items-center gap-3.5 sm:gap-6 md:gap-8 text-white/95">
              <a
                href="tel:+919820234033"
                className="inline-flex items-center gap-1.5 text-white/90 hover:text-white transition-colors cursor-pointer"
                title="Call Simatech Automation"
              >
                <Phone className="w-3.5 h-3.5 text-[#38c9de] shrink-0" />
                <span className="whitespace-nowrap font-sans">+91 9820234033</span>
              </a>
              <a
                href={`mailto:${brand?.emails?.[0] || 'sdd@simatechautomation.com'}`}
                className="hidden sm:inline-flex items-center gap-1.5 text-white/90 hover:text-white transition-colors cursor-pointer"
                title="Email Simatech Automation"
              >
                <Mail className="w-3.5 h-3.5 text-[#38c9de] shrink-0" />
                <span className="whitespace-nowrap font-sans">{brand?.emails?.[0] || 'sdd@simatechautomation.com'}</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Top Navigation Bar: Positioned below top bar at top-9 */}
      <header
        className={`fixed left-0 right-0 z-40 w-full top-9 transition-all duration-300 pointer-events-auto ${isSolidNav
            ? 'bg-white shadow-[0_4px_24px_rgba(0,0,0,0.06)] border-b border-slate-200/80 py-2 sm:py-2.5'
            : 'bg-transparent border-b border-transparent py-2 sm:py-2.5'
          }`}
      >
        <div className="max-w-[1440px] mx-auto px-4 sm:px-8 md:px-12 lg:px-16 w-full pointer-events-auto">
          <div
            className="w-full transition-all duration-300 py-1.5 sm:py-2 min-h-[52px] sm:min-h-[58px] flex items-center justify-between border-0 bg-transparent"
          >
            {/* Left Brand Logo */}
            <a
              href="#home"
              onClick={(e) => handleLink(e, 'home', 'home')}
              className="flex items-center shrink-0 py-0.5 cursor-pointer"
              aria-label="Simatech Automation Home"
            >
              <SimatechLogo
                isWhite={!isSolidNav}
                iconClassName="h-8 sm:h-9 md:h-10 w-auto"
              />
            </a>

            {/* Right-Aligned Navigation Bar Links */}
            <nav className="hidden md:flex items-center justify-end gap-5 lg:gap-7 xl:gap-8 ml-auto font-['General_Sans',sans-serif]">
              {navItems.length > 0
                ? navItems.filter((item) => item.visible).map((item) => {
                  const isItemActive = isNavItemActive(item.page);
                  const isSiemensPartner =
                    item.page === 'siemens-solution-partner' ||
                    item.id === 'nav-siemens-solution-partner' ||
                    item.label.toLowerCase().includes('siemens');

                  if (isSiemensPartner) {
                    return (
                      <a
                        key={item.id}
                        href={getNavHref(item.page)}
                        onClick={(e) => handleNavItemClick(e, item.page)}
                        className={`text-[14px] font-semibold transition-all px-3 py-1.5 cursor-pointer rounded-[2px] border ${isItemActive
                            ? isSolidNav
                              ? 'bg-[#104b54] text-white border-[#104b54] shadow-xs'
                              : 'bg-white/20 text-white border-white shadow-xs'
                            : isSolidNav
                              ? 'text-[#104b54] border-[#104b54] hover:bg-[#104b54] hover:text-white shadow-2xs'
                              : 'text-white border-white bg-white/10 hover:bg-white/20 shadow-2xs'
                          }`}
                        style={{ borderWidth: '1px', borderRadius: '2px' }}
                        title="Siemens Authorized Solution Partner"
                      >
                        {item.label}
                      </a>
                    );
                  }

                  return (
                    <a
                      key={item.id}
                      href={getNavHref(item.page)}
                      onClick={(e) => handleNavItemClick(e, item.page)}
                      className={`text-[15px] font-normal transition-all py-1.5 cursor-pointer ${isItemActive
                          ? isSolidNav
                            ? 'text-[#104b54] font-semibold'
                            : 'text-white font-semibold'
                          : isSolidNav
                            ? 'text-[#06242a] hover:text-[#104b54]'
                            : 'text-white hover:text-[#38c9de]'
                        }`}
                    >
                      {item.label}
                    </a>
                  );
                })
                : (
                  <>
                    <a href="/" onClick={(e) => handleLink(e, 'home', 'home')} className="text-[15px] py-1.5 cursor-pointer">Home</a>
                    <a href="/about" onClick={(e) => handleLink(e, 'about')} className="text-[15px] py-1.5 cursor-pointer">About Us</a>
                    <a href="/services" onClick={(e) => handleLink(e, 'services')} className="text-[15px] py-1.5 cursor-pointer">Services</a>
                    <a href="/industry" onClick={(e) => handleLink(e, 'industry')} className="text-[15px] py-1.5 cursor-pointer">Industry</a>
                    <a href="/product" onClick={(e) => handleLink(e, 'product')} className="text-[15px] py-1.5 cursor-pointer">Products</a>
                    <a href="/clients" onClick={(e) => handleLink(e, 'clients')} className="text-[15px] py-1.5 cursor-pointer">Clients</a>
                    <a href="/contact" onClick={(e) => handleLink(e, 'contact')} className="text-[15px] py-1.5 cursor-pointer">Contact</a>
                    <a
                      href="/siemens-solution-partner"
                      onClick={(e) => handleLink(e, 'siemens-solution-partner')}
                      className={`text-[14px] font-semibold transition-all px-3 py-1.5 cursor-pointer rounded-[2px] border ${currentPage === 'siemens-solution-partner'
                          ? isSolidNav
                            ? 'bg-[#166572] text-white border-[#166572]'
                            : 'bg-white/20 text-white border-white'
                          : isSolidNav
                            ? 'text-[#166572] border-[#166572] hover:bg-[#166572] hover:text-white'
                            : 'text-white border-white/90 hover:bg-white/10'
                        }`}
                      style={{ borderWidth: '1px', borderRadius: '2px' }}
                    >
                      Siemens Solution Partner
                    </a>
                  </>
                )}
            </nav>

            {/* Mobile Hamburger Toggle */}
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`md:hidden p-2 transition-colors ${isSolidNav
                    ? 'bg-slate-100 text-[#06242a] hover:bg-slate-200'
                    : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                aria-label="Toggle navigation menu"
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation Dropdown Overlay */}
          {isMobileMenuOpen && (
            <div
              className={`md:hidden mt-2 p-4 backdrop-blur-2xl space-y-3 animate-in fade-in slide-in-from-top-2 duration-200 shadow-xl font-['General_Sans',sans-serif] ${isSolidNav
                  ? 'bg-white/98 border border-slate-200 text-[#06242a]'
                  : 'bg-black/95 border border-white/15 text-white'
                }`}
            >
              {navItems.length > 0
                ? navItems.filter((item) => item.visible).map((item) => {
                  const isItemActive = isNavItemActive(item.page);
                  const isSiemensPartner =
                    item.page === 'siemens-solution-partner' ||
                    item.id === 'nav-siemens-solution-partner' ||
                    item.label.toLowerCase().includes('siemens');

                  if (isSiemensPartner) {
                    return (
                      <a
                        key={item.id}
                        href={getNavHref(item.page)}
                        onClick={(e) => handleNavItemClick(e, item.page)}
                        className={`block px-3 py-2 text-sm font-semibold rounded-[2px] border transition-all ${isItemActive
                            ? isSolidNav
                              ? 'bg-[#166572] text-white border-[#166572]'
                              : 'bg-white/20 text-white border-white'
                            : isSolidNav
                              ? 'text-[#166572] border-[#166572] hover:bg-[#166572]/10'
                              : 'text-white border-white/80 hover:bg-white/10'
                          }`}
                        style={{ borderWidth: '1px', borderRadius: '2px' }}
                      >
                        {item.label}
                      </a>
                    );
                  }

                  return (
                    <a
                      key={item.id}
                      href={getNavHref(item.page)}
                      onClick={(e) => handleNavItemClick(e, item.page)}
                      className={`block px-3 py-2 text-sm font-medium ${isItemActive
                          ? 'bg-[#1b7481]/10 text-[#1b7481] font-semibold'
                          : isSolidNav ? 'hover:bg-slate-100' : 'hover:bg-white/10'
                        }`}
                    >
                      {item.label}
                    </a>
                  );
                })
                : (
                  <>
                    <a
                      href="/"
                      onClick={(e) => handleLink(e, 'home', 'home')}
                      className={`block px-3 py-2 text-sm font-medium ${isSolidNav ? 'hover:bg-slate-100' : 'hover:bg-white/10'
                        }`}
                    >
                      Home
                    </a>
                    <a
                      href="/about"
                      onClick={(e) => handleLink(e, 'about')}
                      className={`block px-3 py-2 text-sm font-medium ${currentPage === 'about'
                          ? 'bg-[#1b7481]/10 text-[#1b7481]'
                          : isSolidNav ? 'hover:bg-slate-100' : 'hover:bg-white/10'
                        }`}
                    >
                      About Us
                    </a>
                    <a
                      href="/services"
                      onClick={(e) => handleLink(e, 'services')}
                      className={`block px-3 py-2 text-sm font-medium ${currentPage === 'services'
                          ? 'bg-[#1b7481]/10 text-[#1b7481]'
                          : isSolidNav ? 'hover:bg-slate-100' : 'hover:bg-white/10'
                        }`}
                    >
                      Services & Capabilities
                    </a>
                    <a
                      href="/industry"
                      onClick={(e) => handleLink(e, 'industry')}
                      className={`block px-3 py-2 text-sm font-medium ${currentPage === 'industry'
                          ? 'bg-[#1b7481]/10 text-[#1b7481]'
                          : isSolidNav ? 'hover:bg-slate-100' : 'hover:bg-white/10'
                        }`}
                    >
                      Industry Sectors
                    </a>
                    <a
                      href="/product"
                      onClick={(e) => handleLink(e, 'product')}
                      className={`block px-3 py-2 text-sm font-medium ${currentPage === 'product'
                          ? 'bg-[#1b7481]/10 text-[#1b7481]'
                          : isSolidNav ? 'hover:bg-slate-100' : 'hover:bg-white/10'
                        }`}
                    >
                      Products & Hardware
                    </a>
                    <a
                      href="/clients"
                      onClick={(e) => handleLink(e, 'clients')}
                      className={`block px-3 py-2 text-sm font-medium ${currentPage === 'clients'
                          ? 'bg-[#1b7481]/10 text-[#1b7481]'
                          : isSolidNav ? 'hover:bg-slate-100' : 'hover:bg-white/10'
                        }`}
                    >
                      Clients & Partners
                    </a>
                  </>
                )}
              <a
                href="https://www.partnerfinder.automation.siemens.com/s/siemens-partner-finder?language=en_US&c__placedesc=Mumbai%252C%2520Maharashtra%252C%2520India&c__partnerType=Solution%2520Partner&c__keyword=Simatech%2520Automation&c__lat=18.9582347&c__long=72.8319514&c__country=India&c__countryCode=IN&c__distanceCode=100"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center justify-between px-3 py-2 text-sm font-medium border ${isSolidNav
                    ? 'border-[#1b7481]/30 bg-[#eef7f9] text-[#1b7481]'
                    : 'border-white/20 bg-white/10 text-[#38c9de]'
                  }`}
              >
                <span>Siemens Solution Partner (Official)</span>
                <ArrowUpRight className="w-4 h-4 text-[#38c9de]" />
              </a>
              <a
                href="/contact"
                onClick={(e) => handleLink(e, 'contact')}
                className={`group flex items-center justify-between px-3.5 py-2.5 rounded-none text-sm font-medium transition-all duration-200 bg-transparent border border-[#1b7481] text-[#1b7481] hover:bg-[#1b7481] hover:text-white`}
              >
                <span>Connect & Contact</span>
                <ArrowUpRight className="w-4 h-4 opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
              <button
                type="button"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  onOpenInquiry('Turnkey Automation Project');
                }}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#1b7481] text-white font-medium text-xs shadow-md hover:bg-[#06242a] transition-colors cursor-pointer"
              >
                <Sparkles className="w-4 h-4" />
                <span>Get Instant Quote</span>
              </button>
            </div>
          )}
        </div>
      </header>
    </>
  );
};
